import { useEffect, useState, type FC } from 'react';
import { Modal, Space, Button, InputText } from '@g2u/ui-kit';
import { Cascader, Form, Select, Divider } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import { JsonDataItem, type WithOptionalDataTest } from 'types';
import { useDiscountForm } from '../hooks/useDiscountForm';
import { useDiscountOptions, useDiscountData } from 'helpers';
import { Section } from 'components';
import styles from '../payment.module.scss';
import { useWatch } from 'antd/es/form/Form';

type Props = {
  customerId?: number;
  newCustomerDiscounts?: JsonDataItem | null;
  onSaveNewCustomerDicounts: (formData?: JsonDataItem | null) => void;
} & WithOptionalDataTest;

export const Discount: FC<Props> = (props) => {
  const { customerId, onSaveNewCustomerDicounts, newCustomerDiscounts } = props;
  const [showDiscountModal, setShowDiscountModal] = useState<boolean>(false);
  const {
    form,
    isFormValid,
    discountApproverList,
    systemDiscounts,
    isDiscountsLoading,
    isDiscountsAdding,
    addDiscounts,
    formatSavedDiscounts,
    appliedDiscounts,
    isSuccessfullyAdded,
  } = useDiscountForm(customerId);
  const excludeDiscounts: number[] = appliedDiscounts
    ? appliedDiscounts.map((item: JsonDataItem) => item.discountId)
    : [];
  const { discountOptions, getLabel, extraReasonDiscounts } = useDiscountOptions(systemDiscounts, excludeDiscounts);
  const { discountReasons } = useDiscountData();

  const handleFormSubmit = (formData: JsonDataItem | null) => {
    if (customerId) {
      addDiscounts(formData);
    } else {
      onSaveNewCustomerDicounts(formData);
      setShowDiscountModal(false);
    }
  };

  useEffect(() => {
    isSuccessfullyAdded && setShowDiscountModal(false);
  }, [isSuccessfullyAdded]);

  const discountsList = appliedDiscounts?.length ? appliedDiscounts : formatSavedDiscounts(newCustomerDiscounts);

  const selectedDiscount = useWatch('discounts', form);

  return (
    <Section title="Discount" loading={isDiscountsLoading}>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small}>
        {discountsList?.map((discount: JsonDataItem) => (
          <InputText.$
            name={`discount-${discount.discountId}`}
            key={discount.discountId}
            initialValue={`${getLabel(discount.type, discount.amount)} - ${discount.label}, approved by ${discount.approvedBy}`}
            readonly
            width={500}
          />
        ))}
        <Button.$ dataTestId="add-another-discount" onClick={() => setShowDiscountModal(true)}>
          Add New Discount
        </Button.$>
      </Space.$>

      <Modal.$
        open={showDiscountModal}
        destroyOnClose
        maskClosable={false}
        title={'Add Discount'}
        okText="Save"
        onOk={() => {
          form.submit();
        }}
        okButtonProps={{
          loading: isDiscountsAdding,
          disabled: !isFormValid,
        }}
        cancelButtonProps={{
          style: { display: 'none' },
        }}
        dataTestId={'add-discount-modal'}
        onCancel={() => setShowDiscountModal(false)}
      >
        <Form
          form={form}
          name="add-discount-form"
          layout="horizontal"
          onFinish={handleFormSubmit}
          colon={false}
          preserve={!customerId}
          initialValues={{ discounts: [{}] }}
          className={styles.payment__discount}
        >
          <Form.List name="discounts">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space.$ key={key} direction={Space.Direction.Vertical} size={Space.Size.Middle}>
                    <Space.$>
                      <Form.Item
                        {...restField}
                        name={[name, 'discountId']}
                        label={<div className={styles.payment__discountLabel}>Discount :</div>}
                        rules={[{ required: true, message: 'Please select a discount' }]}
                        className={styles.payment__discountItem}
                      >
                        <Cascader
                          options={discountOptions as any}
                          placeholder="Select"
                          changeOnSelect={false}
                          displayRender={(labels: string[]) => labels[labels.length - 1]}
                          className={styles.payment__discountSelect}
                        />
                      </Form.Item>

                      {fields.length > 1 ? (
                        <Button.$
                          onClick={() => remove(name)}
                          danger
                          dataTestId="delete-discount"
                          size={Button.Size.Small}
                        >
                          <DeleteOutlined />
                        </Button.$>
                      ) : null}
                    </Space.$>
                    <Space.$ direction={Space.Direction.Vertical} compact>
                      {selectedDiscount?.[name]?.discountId?.some((discountId: JsonDataItem) =>
                        extraReasonDiscounts.some((discount) => discount.customer_discount_id === discountId),
                      ) && (
                        <Form.Item
                          {...restField}
                          name={[name, 'extraReason']}
                          label={<div className={styles.payment__discountLabel}>Discount Reason :</div>}
                          rules={[{ required: true, message: 'Please select a reason' }]}
                          className={styles.payment__discountItem}
                        >
                          <Select
                            placeholder="Select"
                            optionFilterProp="children"
                            className={styles.payment__discountSelect}
                            options={discountReasons?.map((option: { reason_desc: string; ID: number }) => ({
                              label: option.reason_desc,
                              value: option.ID,
                            }))}
                          />
                        </Form.Item>
                      )}
                    </Space.$>
                    <Space.$ direction={Space.Direction.Vertical} compact>
                      <Form.Item
                        {...restField}
                        name={[name, 'approvedBy']}
                        label={<div className={styles.payment__discountLabel}>Approved by :</div>}
                        rules={[{ required: true, message: 'Please select an approver' }]}
                        className={styles.payment__discountItem}
                      >
                        <Select
                          placeholder="Select"
                          optionFilterProp="children"
                          className={styles.payment__discountSelect}
                          options={discountApproverList?.map((approver: JsonDataItem) => {
                            return {
                              label: approver.name,
                              value: approver.ID?.toString(),
                              details: approver,
                            };
                          })}
                        />
                      </Form.Item>
                      {name < fields.length - 1 ? <Divider /> : null}
                    </Space.$>
                  </Space.$>
                ))}
                <Form.Item className={styles.payment__discountAction}>
                  <Button.$ dataTestId="add-new-discount" onClick={() => add()}>
                    Add another
                  </Button.$>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal.$>
    </Section>
  );
};
